import { useMemo } from "react";
import { Card } from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import {
  usePoolFormation,
  usePoolTriggers,
} from "@/hooks/capitalMarkets/useSpvPools";
import type {
  PoolSegmentTriggerEventDto,
  SpvPoolDto,
} from "@/types/spvPool";

interface FormationHistoryTabProps {
  pool: SpvPoolDto;
}

/**
 * Payment System v3.0 PR 6 — pool formation history. Shows the
 * PoolFormationEvent and the per-loan PoolSegmentTriggerEvent rows,
 * grouped by trigger source (formation vs each closing).
 */
export function FormationHistoryTab({ pool }: FormationHistoryTabProps) {
  const formationQuery = usePoolFormation(pool.id);
  const triggersQuery = usePoolTriggers(pool.id);
  const { dateTime } = useDateFormatter();

  const formation = formationQuery.data ?? null;
  const triggers = useMemo(
    () => triggersQuery.data ?? [],
    [triggersQuery.data],
  );

  const groupedTriggers = useMemo(() => groupTriggers(triggers), [triggers]);

  if (formationQuery.isLoading || triggersQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          Loading formation history…
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-4">
      <Card>
        <h3 className="font-display font-bold text-h3 m-0">
          Formation Ceremony
        </h3>
        {formation === null ? (
          <p className="text-small text-gray-mid mt-space-2 mb-0">
            This pool has not been finalized yet. Run the Pool Formation
            Wizard to file the CSS and fire the S1 cascade.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-3 mt-space-3">
            <Field label="Formation Date" value={formation.formationDate} />
            <Field
              label="Roster Loan Count"
              value={formation.rosterLoanCount.toString()}
            />
            <Field
              label="CSS Filed"
              value={formation.cssFiled ? "Yes" : "Pending"}
            />
            <Field
              label="Formed By"
              value={formation.formedByUserId ?? "—"}
            />
            <Field
              label="CSS Document"
              value={
                formation.cssDocumentId
                  ? formation.cssDocumentId.slice(0, 8) + "…"
                  : "—"
              }
            />
            <Field
              label="Recorded At"
              value={dateTime(formation.createdAt)}
            />
          </div>
        )}
      </Card>

      <Card>
        <h3 className="font-display font-bold text-h3 m-0">
          Segment Trigger Events
        </h3>
        {triggers.length === 0 ? (
          <p className="text-small text-gray-mid mt-space-2 mb-0">
            No segment triggers have fired for this pool yet.
          </p>
        ) : (
          <div className="flex flex-col gap-space-3 mt-space-3">
            {groupedTriggers.map(([groupKey, rows]) => (
              <div
                key={groupKey}
                className="rounded-md border border-border-light p-space-3"
              >
                <h4 className="font-display font-bold text-onyx text-small m-0">
                  {groupKey}
                </h4>
                <table className="w-full mt-space-2 text-small">
                  <thead>
                    <tr className="text-left text-gray-mid">
                      <th className="font-normal py-space-1">Loan</th>
                      <th className="font-normal py-space-1">Segment</th>
                      <th className="font-normal py-space-1">Source</th>
                      <th className="font-normal py-space-1">Date</th>
                      <th className="font-normal py-space-1">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <tr key={t.id} className="border-t border-border-light">
                        <td className="py-space-2 font-mono text-[11px]">
                          {t.loanId.slice(0, 8)}…
                        </td>
                        <td className="py-space-2">S{t.segmentNumber}</td>
                        <td className="py-space-2">{t.triggerSource}</td>
                        <td className="py-space-2">{t.triggerDate}</td>
                        <td className="py-space-2">
                          {t.isReversed ? (
                            <span className="text-gray-mid">Reversed</span>
                          ) : (
                            <span className="text-gold-shadow">Active</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-label uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="text-small text-onyx mt-space-1">{value}</div>
    </div>
  );
}

function groupTriggers(
  rows: PoolSegmentTriggerEventDto[],
): Array<[string, PoolSegmentTriggerEventDto[]]> {
  const map = new Map<string, PoolSegmentTriggerEventDto[]>();
  for (const row of rows) {
    const key =
      row.triggerSource === "pool_formation"
        ? `Pool Formation — ${row.triggerDate}`
        : `Investor Closing — ${row.triggerDate}` +
          (row.closingPaymentId
            ? ` (${row.closingPaymentId.slice(0, 8)}…)`
            : "");
    const list = map.get(key) ?? [];
    list.push(row);
    map.set(key, list);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}
